import { AWS_LLM_API } from '@/config/aws';
import {
  APIThread,
  DEFAULT_FILTERS,
  MessageChunk,
  ProcessingAction,
  Thread,
  ThreadItem,
  ThreadProcessingStatus,
  ThreadsPayload,
} from '@/models/thread';
import {
  deleteFromProcessingQueue,
  fetchProcessingQueue,
  isProcessingExpired,
} from '@/pages/Thread/utils/storage';
import { extractPayload, getSubHeader } from '@/utils/api';
import { convertTimestampToString, parseFullMessage } from '@/utils/functions';
import { get } from 'aws-amplify/api';

const fetchPage = async (
  userSub: string,
  page = 1
): Promise<{
  threads: APIThread[];
  totalPages: number;
}> => {
  const { response } = get({
    apiName: AWS_LLM_API,
    path: '/v1/base/conversation/threads',
    options: {
      headers: getSubHeader(userSub),
      queryParams: {
        page: page.toString(),
        perPage: '100',
      },
    },
  });
  const payload = await extractPayload<ThreadsPayload>(response);

  return {
    threads: payload.data.threadData,
    totalPages: payload.data.paginationParams.totalPages,
  };
};

export const fetchAllThreads = async (userSub: string): Promise<Thread[]> => {
  const { threads, totalPages } = await fetchPage(userSub);
  const processingQueue = fetchProcessingQueue();

  if (totalPages > 1) {
    for (let page = 2; page <= totalPages; page++) {
      const { threads: pageThreads } = await fetchPage(userSub, page);

      if (pageThreads.length === 0) {
        break;
      } else {
        threads.push(...pageThreads);
      }
    }
  }

  const allThreads = threads
    .map((thread) => {
      try {
        return mapAPIThreadToThread(thread, processingQueue);
      } catch (error: unknown) {
        console.error(
          `Error mapping API thread to Thread model, thread ID: ${thread.thread_id} `,
          error
        );

        return null;
      }
    })
    .filter((thread) => thread !== null);

  // Append to the beggining to allThreads threads that are not there and are precent in the processingQueue
  processingQueue.forEach((action) => {
    const thread = allThreads.find((t) => t.id === action.threadID);
    if (!thread) {
      allThreads.unshift({
        id: action.threadID,
        title: action.item.prompt,
        items: [
          {
            id: action.item.id,
            disliked: false,
            prompt: action.item.prompt,
            queryDate: new Date(action.ts),
            response: [],
            enabledCitation: true,
            sources: [],
          },
        ],
        filters: DEFAULT_FILTERS,
        processingStatus: ThreadProcessingStatus.ASYNC,
      });
    }
  });

  return allThreads;
};

export const fetchSingleThread = async (
  userSub: string,
  threadId: string
): Promise<Thread | null> => {
  try {
    const { response } = get({
      apiName: AWS_LLM_API,
      path: `/v1/base/conversation/threads/${threadId}`,
      options: {
        headers: getSubHeader(userSub),
      },
    });
    const payload = await extractPayload<{ data: { threadData: APIThread } }>(
      response
    );
    if (!payload.data.threadData) return null;

    const processingQueue = fetchProcessingQueue();

    return mapAPIThreadToThread(payload.data.threadData, processingQueue);
  } catch (error) {
    console.error('Error fetching thread:', error);

    return null;
  }
};

const mapAPIThreadToThread = (
  thread: APIThread,
  processingQueue: ProcessingAction[]
): Thread => {
  let processingStatus: ThreadProcessingStatus | undefined = undefined;

  const pendingThreadAction = processingQueue.find(
    (action) => action.threadID === thread.thread_id
  );

  const items: ThreadItem[] = thread.thread_items.map((item, i) => {
    let enabledCitation = false;
    let response: Array<MessageChunk> = [];
    if (typeof item.citations !== 'undefined') {
      const citedResponse = item.citations.response;

      if (citedResponse?.length > 0) {
        response = citedResponse;
      } else {
        enabledCitation = true;
      }
    }

    if (typeof item.llm_response === 'undefined') {
      if (i === thread.thread_items.length - 1) {
        processingStatus = ThreadProcessingStatus.ASYNC;

        if (pendingThreadAction?.item.id) {
          // Since there's a backend bug where the "processing item" ID is not the real item ID,  we need to update the id with the one stored in the pendingAction
          console.log(
            'overridden message_id:',
            item.message_id,
            'with:',
            pendingThreadAction.item.id
          );
          item.message_id = pendingThreadAction.item.id;
        }
      } else {
        throw new Error(
          `Error processing Thread ${thread.thread_id}, item index: ${i}`
        );
      }
    } else if (response.length === 0) {
      response = parseFullMessage(item.llm_response);
    }
    const res: ThreadItem = {
      id: item.message_id,
      disliked: false,
      prompt: item.user_input,
      queryDate: new Date(item.query_ts),
      response,
      enabledCitation: enabledCitation,
      sources: [...item.rag_sources, ...(item.quantitative_sources || [])],
      // TODO: ask the backend to send `mode` from the single-thread endpoint
      // too. Until then, threads fetched one by one leave this undefined.
      deepDive: item.mode === undefined ? undefined : item.mode === 'auto',
    };

    return res;
  });

  if (pendingThreadAction) {
    const itemIndex = items.findIndex(
      ({ id }) => id === pendingThreadAction.item.id
    );
    const isExpired = isProcessingExpired(new Date(pendingThreadAction.ts));
    if (typeof processingStatus === 'undefined') {
      if (itemIndex === -1) {
        if (!isExpired) {
          processingStatus = ThreadProcessingStatus.ASYNC;
          items.push({
            ...pendingThreadAction.item,
            queryDate: new Date(pendingThreadAction.ts),
            response: [],
          });
        } else {
          deleteFromProcessingQueue(thread.thread_id);
        }
      } else if (
        itemIndex === items.length - 1 &&
        items[itemIndex].response.length === 0 &&
        !isExpired
      ) {
        processingStatus = ThreadProcessingStatus.ASYNC;
      } else {
        deleteFromProcessingQueue(thread.thread_id);
      }
    }
  }

  return {
    id: thread.thread_id,
    title: thread.title,
    processingStatus,
    items,
    filters: {
      countries: thread.filters.countries,
      sources: thread.filters.source_category,
      earliestDate: convertTimestampToString(
        thread.filters.publish_date.earliest
      ),
      latestDate: convertTimestampToString(thread.filters.publish_date.latest),
    },
  };
};
